import { useState } from "react";
import { toast } from "sonner";
import { X, Copy, Check, MessageSquare } from "lucide-react";

const TEMPLATE =
  "Hej {NAVN}. Dit Aarhus Friskole-tøj ligger klar i Anne Sofies rum. Det er det lille rum til venstre i rød Fællesrum. Hvis du ikke ved, hvor det er, kan du spørge nogen på skolen. Skriv eller ring endelig til dette nummer igen, hvis du har flere spørgsmål.";

const firstName = (name) => {
  const n = (name || "").trim().split(/\s+/)[0] || "";
  return n ? n.charAt(0).toUpperCase() + n.slice(1) : "";
};

const buildMessage = (name) => TEMPLATE.replace("{NAVN}", firstName(name));

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  }
};

export const MessageList = ({ orders, onClose }) => {
  const [copied, setCopied] = useState(null);

  const recipients = Object.values(
    orders.reduce((acc, o) => {
      const key = o.phone;
      if (!acc[key]) acc[key] = { phone: o.phone, name: o.customer_name, orderNumbers: [] };
      acc[key].orderNumbers.push(o.order_number);
      return acc;
    }, {})
  ).sort((a, b) => a.name.localeCompare(b.name, "da"));

  const handleCopy = async (key, text) => {
    const ok = await copyText(text);
    if (ok) {
      setCopied(key);
      toast.success("Kopieret");
      setTimeout(() => setCopied(null), 1500);
    } else {
      toast.error("Kunne ikke kopiere");
    }
  };

  const allText = recipients
    .map((r) => `${r.name} – ${r.phone}\n${buildMessage(r.name)}`)
    .join("\n\n----------\n\n");

  return (
    <div
      data-testid="message-list-modal"
      className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-3 sm:p-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b-2 border-slate-200 flex items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2">
              <MessageSquare size={20} /> Beskeder til kunder
            </h2>
            <p className="text-sm text-slate-500 font-semibold">
              {recipients.length} {recipients.length === 1 ? "modtager" : "modtagere"} (samme telefonnummer = én besked)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="btn-copy-all-messages"
              onClick={() => handleCopy("all", allText)}
              className="h-10 px-3 rounded-xl bg-slate-900 text-white font-bold hover:bg-slate-800 active:scale-95 transition-all flex items-center gap-2 text-sm"
            >
              {copied === "all" ? <Check size={16} /> : <Copy size={16} />}
              <span className="hidden sm:inline">Kopiér alle</span>
            </button>
            <button
              data-testid="btn-close-message-list"
              onClick={onClose}
              className="h-10 w-10 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 active:scale-95 transition-all flex items-center justify-center"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3">
          {recipients.length === 0 && (
            <p className="text-center text-slate-400 font-semibold py-10">Ingen bestillinger</p>
          )}
          {recipients.map((r) => {
            const msg = buildMessage(r.name);
            return (
              <div
                key={r.phone}
                data-testid={`message-row-${r.phone}`}
                className="rounded-xl border-2 border-slate-200 p-4 bg-slate-50"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-bold text-slate-900">{r.name}</span>
                    <button
                      data-testid={`btn-copy-phone-${r.phone}`}
                      onClick={() => handleCopy(`phone-${r.phone}`, r.phone)}
                      title="Kopiér telefonnummer"
                      className="font-mono text-sm font-bold text-slate-600 bg-white border border-slate-200 rounded-lg px-2 py-0.5 hover:bg-slate-100 active:scale-95 transition-all flex items-center gap-1"
                    >
                      {r.phone}
                      {copied === `phone-${r.phone}` ? <Check size={12} /> : <Copy size={12} />}
                    </button>
                    <span className="text-xs text-slate-400 font-semibold">
                      {r.orderNumbers.map((n) => `#${n}`).join(", ")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      data-testid={`btn-sms-${r.phone}`}
                      href={`sms:+45${r.phone}${/iPhone|iPad|iPod|Mac/.test(navigator.userAgent) ? "&" : "?"}body=${encodeURIComponent(msg)}`}
                      className="h-9 px-3 rounded-lg bg-emerald-100 text-emerald-700 font-bold text-sm hover:bg-emerald-200 active:scale-95 transition-all flex items-center gap-1"
                    >
                      <MessageSquare size={14} /> Åbn SMS
                    </a>
                    <button
                      data-testid={`btn-copy-message-${r.phone}`}
                      onClick={() => handleCopy(`msg-${r.phone}`, msg)}
                      className="h-9 px-3 rounded-lg bg-slate-900 text-white font-bold text-sm hover:bg-slate-800 active:scale-95 transition-all flex items-center gap-1"
                    >
                      {copied === `msg-${r.phone}` ? <Check size={14} /> : <Copy size={14} />}
                      Kopiér besked
                    </button>
                  </div>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{msg}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
